import { useState, useEffect, useRef } from "react";

const DEFAULT_DURATION_MS = 700;
export const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Hook: returns the current animated value (0 → value) for use with custom layout (e.g. progress bar + number in sync).
 */
export function useAnimatedValue(value = 0, duration = DEFAULT_DURATION_MS) {
  const [displayValue, setDisplayValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Number(value) || 0;
    setDisplayValue(0);
    const start = performance.now();
    const run = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      setDisplayValue(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(run);
    };
    rafRef.current = requestAnimationFrame(run);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration]);

  return displayValue;
}

/**
 * Renders a number that animates from 0 to the target value (counting up).
 * No physical movement - only the displayed number changes.
 *
 * @param {number} value - Target number to count up to
 * @param {number} duration - Animation duration in ms
 * @param {React.ElementType} component - Wrapper component (e.g. Typography, span)
 * @param {object} componentProps - Props passed to the wrapper component
 */
export function AnimatedNumber({
  value = 0,
  duration = DEFAULT_DURATION_MS,
  component: Component = "span",
  ...componentProps
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const target = Number(value) || 0;
    setDisplayValue(0);
    const start = performance.now();

    const run = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      setDisplayValue(Math.round(target * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(run);
      }
    };
    rafRef.current = requestAnimationFrame(run);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [value, duration]);

  return <Component {...componentProps}>{displayValue}</Component>;
}

export default AnimatedNumber;
